import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import { Search, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 50;

// Colour-code the activity based on the verb in the action text
const actionColor = (action = '') => {
  const a = action.toLowerCase();
  if (a.startsWith('login')) return '#22c55e';
  if (a.startsWith('created')) return '#15487d';
  if (a.startsWith('edited')) return '#f59e0b';
  if (a.startsWith('deactivated') || a.startsWith('deleted')) return '#ef4444';
  if (a.startsWith('activated')) return '#22c55e';
  return '#94a3b8';
};

export default function UserLogsPage() {
  const { hasRole } = useAuth();
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState([null, null]);
  const [rangeStart, rangeEnd] = range;
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const dpRef = useRef(null);

  useEffect(() => {
    api.get('/logs').then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [search, appliedFrom, appliedTo]);

  const applyRange = () => {
    setAppliedFrom(rangeStart ? format(rangeStart, 'yyyy-MM-dd') : '');
    setAppliedTo(rangeEnd ? format(rangeEnd, 'yyyy-MM-dd') : '');
    dpRef.current?.setOpen(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = logs.filter((l) => {
    if (q && ![l.name, l.email, l.role, l.action]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return false;
    if (appliedFrom || appliedTo) {
      const d = l.createdAt ? format(new Date(l.createdAt), 'yyyy-MM-dd') : '';
      if (appliedFrom && d < appliedFrom) return false;
      if (appliedTo && d > appliedTo) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageLogs = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!hasRole('super_admin')) {
    return <div className="page"><div className="empty-state">Access denied.</div></div>;
  }

  const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  }) : '—';

  return (
    <div className="page user-logs-page">
      <div className="page-header">
        <div>
          <h2>User Logs</h2>
          <p className="text-muted">Login and activity history - {logs.length}</p>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <DatePicker
            ref={dpRef}
            showIcon
            icon={<Calendar size={16} />}
            selectsRange
            startDate={rangeStart}
            endDate={rangeEnd}
            onChange={(update) => {
              const u = update || [null, null];
              setRange(u);
              if (!u[0] && !u[1]) { setAppliedFrom(''); setAppliedTo(''); }
            }}
            shouldCloseOnSelect={false}
            isClearable
            dateFormat="dd MMM yyyy"
            placeholderText="Select date range"
            className="form-input datepicker-input logs-datepicker"
          >
            <div className="datepicker-footer">
              <button type="button" className="btn btn-sm btn-primary" onClick={applyRange}>OK</button>
            </div>
          </DatePicker>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user, email, role, activity..."
            />
          </div>
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Activity</th>
            </tr>
          </thead>
          <tbody>
            {pageLogs.map((l) => (
              <tr key={l._id}>
                <td>{fmt(l.createdAt)}</td>
                <td><strong>{l.name || '—'}</strong></td>
                <td>{l.email || '—'}</td>
                <td>{(l.role || '').replace('_', ' ')}</td>
                <td style={{ maxWidth: '45ch', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <span className="status-dot" style={{ background: actionColor(l.action) }} />
                  <span className="status-text" style={{ textTransform: 'none' }}>{l.action || '—'}</span>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="empty-state">
                {logs.length === 0 ? 'No logs yet.' : 'No logs match your search.'}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="empty-state">Loading...</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="pagination-controls">
            <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, idx) => p === '...'
                ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                : <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next ›</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}
